import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { PieChart, Pie, Cell, Legend, Tooltip } from "recharts";
import PercentageGain from "../../percentageGain/percentageGain";
import "./stackedPieChart.scss";
import { Tooltip as ReactTooltip } from "react-tooltip";

import { asNumberString } from "../../../utils/formatters";

const formatLegendText = (revenue) => {
  return (
    <span>
      {revenue !== "No Data" && (
        <span
          className={`lengend-icon-${revenue}`}
          style={{ fontSize: 18, verticalAlign: "text-top" }}
        >
          {revenue}
        </span>
      )}
    </span>
  );
};

const StackedPieChart = (props) => {
  const [showLegendTooltip, setShowLegendTooltip] = useState(false);

  let haveNoData = null;
  const {
    data = [],
    colors = ["#2651ac", "#95adf7", "#4667d4", "#6d8ae6"],
    chartStats = [],
    width = 400,
    height = 360,
    isCurrency = false,
    clickablePie,
    hideLegendMetrics,
    legendsTooltipId = ""
  } = props;

  useEffect(() => {
    const legendsWrapper = document.querySelector(`[data-tooltip-id=${legendsTooltipId}]`);
    const legendsWrapperHeight = legendsWrapper.offsetHeight;
    if (legendsWrapperHeight > 26) {
      setShowLegendTooltip(true);
    }
  }, []);

  const Label = () => {
    return (
      <div className="label-container">
        {chartStats.map((chartStat) => (
          <div key={chartStat.title} style={{ textAlign: "center", zIndex: 1 }}>
            <p className="label-title" style={{ margin: "0" }}>
              {chartStat.title}
            </p>
            <p className="label-value" style={{ margin: "0 0 5px 0" }}>
              {chartStat.valueRawDataLink ? (
                <a
                  href={chartStat.valueRawDataLink}
                  style={{ fontWeight: 500 }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {typeof chartStat.value === "string"
                    ? chartStat.value
                    : asNumberString(chartStat.value)}
                </a>
              ) : typeof chartStat.value === "string" ? (
                chartStat.value
              ) : (
                asNumberString(chartStat.value)
              )}
            </p>
            <PercentageGain
              negIsPositive={chartStat.negIsPositive === true}
              value={(parseFloat(chartStat.percentChange) * 100).toFixed(1)}
            />
          </div>
        ))}
      </div>
    );
  };

  const toolTipFormatter = (value, name, props) => {
    return `${props.payload.isCurrency ? "$" : ""}` + asNumberString(value);
  };

  const cleanedData = (data) => {
    const cleanedData = [];
    let zeroCount = 0;

    if (Array.isArray(data)) {
      data.forEach((item) => {
        if (!item.revenue) {
          zeroCount++;
        }
        cleanedData.push(item);
      });
    }

    if (zeroCount === cleanedData.length) {
      cleanedData.push({ revenueType: "No Data", revenue: 1 });
      haveNoData = true;
    }

    return cleanedData;
  };

  const displayMetrics = (item) => {
    if (!hideLegendMetrics) {
      return `: ${isCurrency ? "$" : ""}${asNumberString(item.revenue)}`;
    } else {
      return "";
    }
  };

  const returnLegendPayload = (data) => {
    let legendData = [];
    data.forEach((item, index) => {
      if (item.revenueType !== "No Data") {
        legendData.push({
          id: item.revenueType,
          color: colors[index % colors.length],
          type: "circle",
          value: `${item.revenueType}${displayMetrics(item)}`
        });
      }
    });
    return legendData;
  };

  const handleClick = (event) => {
    if (clickablePie & (event.revenueType !== "No Data")) {
      window.open(event.url);
    }
  };

  return (
    <div className="pie-chart-container">
      <Label />
      <PieChart width={width} height={height}>
        <Pie
          data={cleanedData(data)}
          innerRadius={95}
          outerRadius={130}
          startAngle={90}
          endAngle={-360}
          dataKey="revenue"
          nameKey="revenueType"
          onClick={(event) => handleClick(event)}
        >
          {cleanedData(data).map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={
                entry.revenueType === "No Data" ? "rgba(0,0,0,0.2)" : colors[index % colors.length]
              }
              strokeWidth={0}
              isCurrency={isCurrency}
              style={{ cursor: entry.url !== undefined ? "pointer" : "inherit" }}
            />
          ))}
        </Pie>

        {!haveNoData && <Tooltip formatter={toolTipFormatter} />}
      </PieChart>
      <div
        className={`legends-wrapper ${showLegendTooltip ? "ellipsis-text" : ""}`}
        data-tooltip-id={legendsTooltipId}
      >
        <Legend
          height="0"
          payload={returnLegendPayload(data)}
          verticalAlign="bottom"
          align="center"
          iconType="circle"
          iconSize={13}
          formatter={formatLegendText}
          wrapperStyle={{
            bottom: "40px"
          }}
        />
      </div>
      {showLegendTooltip ? (
        <ReactTooltip id={legendsTooltipId} place="bottom" class="legends-tooltip">
          <div style={{ maxWidth: "800px" }}>
            <Legend
              height="0"
              payload={returnLegendPayload(data)}
              verticalAlign="bottom"
              align="center"
              iconType="circle"
              iconSize={13}
              formatter={formatLegendText}
              wrapperStyle={{
                bottom: "40px",
                paddingTop: 10
              }}
            />
          </div>
        </ReactTooltip>
      ) : null}
    </div>
  );
};

StackedPieChart.propTypes = {
  data: PropTypes.array.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  chartStats: PropTypes.array,
  colors: PropTypes.array,
  viewBox: PropTypes.any,
  index: PropTypes.number,
  title: PropTypes.string,
  value: PropTypes.any,
  valueRawDataLink: PropTypes.any,
  percentChange: PropTypes.any,
  payload: PropTypes.object,
  isCurrency: PropTypes.bool,
  clickablePie: PropTypes.bool,
  hideLegendMetrics: PropTypes.bool
};

export default React.memo(StackedPieChart);
